import { useCommunity } from '~/contexts/CommunityContext'
import {
  GetCustomerActivityPostsDocument,
  GetCustomerActivityPostsFullDocument,
  GetCustomerActivityPostsQuery,
  GetCustomerActivityPostsQueryVariables,
} from '~/api/generated/graphql'
import '@css/pages/company/CustomerActivity.scss'
import React, { useMemo, useState } from 'react'
import { SizeBreakpoint, useWindowSize } from '~/common/hooks/useWindowSize'
import { CustomerActivityList } from '~/pages/company/CustomerActivityList'
import { useLoadObjUsers } from '~/common/hooks/useLoadObjUsers'
import { useBackgroundFetch } from '~/common/hooks/useBackgroundFetch'
import { useNavigate } from 'react-router'

const PAGE_SIZE = 25

export const CustomerActivity = () => {
  const { companyId, companyName, isVeeva, loading: loadingHomepage } = useCommunity()

  const [postExpanded, setPostExpanded] = useState<boolean>(false)

  const { breakpoint } = useWindowSize()
  const isCondensed = breakpoint <= SizeBreakpoint.lg

  const {
    previewData: customerPostsData,
    loading: customerPostsLoading,
    loadMore: customerLoadMore,
    hasMore: customerHasMore,
    fullData: fullCustomerData,
    error,
  } = useBackgroundFetch<GetCustomerActivityPostsQuery, GetCustomerActivityPostsQueryVariables>(
    GetCustomerActivityPostsDocument,
    GetCustomerActivityPostsFullDocument,
    {
      variables: { pageSize: PAGE_SIZE ?? 25, companyId: companyId ?? '' },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !companyId,
    },
    !postExpanded
  )

  const {
    loading: loadingCustomerUsers,
    processedPostIds: processedCustomerPostIds,
    loadingFirstPage: loadingFirstCustomerUsers,
  } = useLoadObjUsers(customerPostsData?.posts, 'postId')

  useLoadObjUsers(fullCustomerData?.posts, 'postId')

  const activities = useMemo(() => {
    if (customerPostsData) {
      return customerPostsData?.posts?.edges
        ?.map(p => p?.node?.postId)
        .filter(id => Boolean(id) && processedCustomerPostIds?.has(id ?? '')) as string[]
    }
  }, [customerPostsData, processedCustomerPostIds])

  const loading = (customerPostsLoading && !customerPostsData) || loadingFirstCustomerUsers
  const navigate = useNavigate()

  if (loadingHomepage) {
    return <></>
  }

  if (isVeeva) {
    void navigate('/')
    return <></>
  }

  return (
    <div className={'customer-activity-container'}>
      <h3>Customer Activity</h3>
      <div className={`recent-activity ${isCondensed ? 'condensed' : ''}`}>
        {!!companyName && (
          <>
            <i className={'activity-visibility-statement'}>
              This page displays the Veeva Connect activity for users from {companyName}. This page is visible to users
              from Veeva and {companyName}.
            </i>
            <h3 className={'activity-header'}>{companyName}'s Community Activity</h3>
            <CustomerActivityList
              postIds={activities ?? []}
              setPostExpanded={setPostExpanded}
              hasMore={customerHasMore}
              loadMore={customerLoadMore}
              loadingMore={customerPostsLoading || loadingCustomerUsers}
              loading={loading}
              error={error}
            />
          </>
        )}
      </div>
    </div>
  )
}
